/**
 * CMSPage.jsx - Generic CMS-driven Page
 * ======================================
 * Displays content from blueprint_pages table
 * 100% dynamic - NO HARDCODED TEXT
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CMSPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get slug from params or from pathname
  const slug = paramSlug || location.pathname.replace('/', '');

  useEffect(() => {
    const loadPage = async () => {
      if (!slug) {
        setError('Pagina non trovata');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('blueprint_pages')
          .select('*')
          .eq('slug', slug)
          .eq('is_published', true)
          .single();

        if (fetchError || !data) {
          setError('Pagina non trovata');
        } else {
          setPage(data);
        }
      } catch (err) {
        console.error('Error loading page:', err);
        setError('Errore nel caricamento della pagina');
      }
      setLoading(false);
    };

    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#0a0a0a] pt-24">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-20">
            <div className="text-center">
              <h1 className="text-4xl font-light text-white mb-4">404</h1>
              <p className="text-white/60 mb-8">{error || 'Pagina non trovata'}</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-[#B08B5C] text-black rounded-lg hover:bg-[#9a7a50] transition-colors"
              >
                Torna alla Home
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Get localized content
  const title = page.title_it || page.title;
  const description = page.description || '';
  const content = page.content || {};

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#0a0a0a]">
        {/* Hero Section */}
        <section className="pt-32 pb-16 border-b border-white/5">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12">
            <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
              {title}
            </h1>
            {description && (
              <p className="text-white/60 text-lg max-w-2xl">
                {description}
              </p>
            )}
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            {/* If content is HTML string */}
            {typeof content === 'string' && content && (
              <div 
                className="prose prose-invert prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}

            {/* If content is structured JSON */}
            {typeof content === 'object' && content.sections && (
              <div className="space-y-12">
                {content.sections.map((section, index) => (
                  <div key={index} className="space-y-4">
                    {section.title && (
                      <h2 className="text-2xl font-light text-white">
                        {section.title}
                      </h2>
                    )}
                    {section.body && (
                      <div 
                        className="text-white/70 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: section.body }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Placeholder if no content */}
            {(!content || (typeof content === 'object' && !content.sections)) && (
              <div className="text-center py-12">
                <p className="text-white/40">
                  Contenuto in arrivo...
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CMSPage;
